import cleanGatewayResponse from "./cleanGatewayResponse";
import { DocumentChunk } from "../../types/interfaces/IConversationSettings";

/**
 * Converts a base64 string to a Blob URL for more efficient handling of large images
 * 
 * @param {string} base64String - The base64 string to convert to a Blob URL
 * @param {string} mimeType - The MIME type of the image (default: "image/png")
 * @return {string} A Blob URL that can be used in an img src attribute
 */
function createBlobUrlFromBase64(base64String: string, mimeType: string = "image/png"): string {
  try {
    // Remove data URL prefix if present
    const base64Data = base64String.includes('base64,') 
      ? base64String.split('base64,')[1] 
      : base64String;
      
    // Create a Blob from the base64 data
    const byteCharacters = atob(base64Data);
    const byteArrays = [];
    
    for (let offset = 0; offset < byteCharacters.length; offset += 1024) {
      const slice = byteCharacters.slice(offset, offset + 1024);
      const byteNumbers = new Array(slice.length);
      
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }
      
      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }
    
    const blob = new Blob(byteArrays, { type: mimeType });
    return URL.createObjectURL(blob);
  } catch (error) {
    console.error("Error creating blob URL from base64:", error);
    return "";
  }
}

/**
 * Reads chunks from a ReadableStream and processes them using a callback function.
 *
 * @param {ReadableStream} stream - The ReadableStream to read data from.
 * @param {(token: string) => void} onNewChunk - Callback function that receives each chunk as it arrives.
 * @param {string} [modelName=""] - Optional model name to help with processing.
 * @param {(chunks: DocumentChunk[]) => void} [onDocumentChunks] - Callback for when document chunks are received.
 *
 * @return {Promise<{chunks: string, citations: string[], documentChunks?: DocumentChunk[]}>} - Response with text, citations, and document chunks.
 */
export default async function readMessageStream(
  stream: ReadableStream,
  onNewChunk: (token: string) => void = () => {}, // callback function gets chunks as they arrive
  modelName: string = "", // Add model name to help with processing
  onDocumentChunks: (chunks: DocumentChunk[]) => void = () => {}, // callback for document chunks
): Promise<{chunks: string, citations: string[], documentChunks?: DocumentChunk[]}> {
  const reader = stream.getReader();
  const chunks: string[] = [];
  let citations: string[] = [];
  let documentChunks: DocumentChunk[] = [];
  let accumulatedDocumentChunks: DocumentChunk[] = []; // For batched responses
  let expectedBatches = 0;
  let receivedBatches = 0;
  
  // For GPT-image-1 responses, we need to accumulate binary data
  const isGptImageModel = modelName && modelName.toLowerCase().includes("gpt-image");
  let accumulatedData: Uint8Array | undefined = undefined;
  let totalBytes = 0;
  let lastChunkTime = Date.now();
  const CHUNK_TIMEOUT_MS = 5000; // 5 seconds timeout between chunks
  
  // Initial message if it's a GPT-image model
  if (isGptImageModel) {
    const initialMessage = "Image is being generated...";
    onNewChunk(initialMessage);
    console.log("Processing GPT-image-1 response");
    
    // Store the original user prompt
    const userPrompt = modelName.split(":")[1]?.trim();
    if (userPrompt) {
      console.log("Stored original user prompt for GPT-image-1:", userPrompt);
    }
  }
  
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        console.log("Stream read complete");
        break;
      }
      
      // Skip processing for empty values
      if (!value || value.length === 0) continue;

      // Add to total bytes count for logging
      totalBytes += value.length;
      lastChunkTime = Date.now();
      lastChunkTime = Date.now();
      console.log(`Received chunk: ${value.length} bytes, total: ${totalBytes} bytes`);
      
      // Debug: Log large chunks that might contain document data
      if (value.length > 5000) {
        console.log("DEBUG: Large chunk received, first 200 chars:", new TextDecoder().decode(value.slice(0, 200)));
      }
      
      // Process based on model type
      if (isGptImageModel) {
        // For GPT-image models, we need to accumulate binary data
        // because the image might be split across multiple chunks
        if (accumulatedData === undefined) {
          accumulatedData = value;
        } else {
          // Combine the arrays
          const newLength = accumulatedData.length + value.length;
          const newArray = new Uint8Array(newLength);
          newArray.set(accumulatedData);
          newArray.set(value, accumulatedData.length);
          accumulatedData = newArray;
        }
        
        // Make sure accumulatedData is defined before accessing length
        if (accumulatedData) {
          console.log(`Accumulated data size: ${accumulatedData.length} bytes`);
          
          // Check if we have a complete JSON response by looking for proper JSON structure
          const decodedData = new TextDecoder().decode(accumulatedData);
          
          // For GPT-image responses, we need to wait for a complete JSON object
          // Check if the JSON is complete by counting braces and looking for proper ending
          const openBraces = (decodedData.match(/\{/g) || []).length;
          const closeBraces = (decodedData.match(/\}/g) || []).length;
          const hasCompleteJson = openBraces > 0 && openBraces === closeBraces && 
                                 decodedData.trim().endsWith('}');
          
          // Also check if we have a complete b64_json field (ends with proper base64 padding)
          const b64JsonMatch = decodedData.match(/"b64_json"\s*:\s*"([^"]+)"/);
          const hasCompleteB64 = b64JsonMatch && b64JsonMatch[1] && 
                                (b64JsonMatch[1].endsWith('=') || b64JsonMatch[1].endsWith('==') || 
                                 /[A-Za-z0-9+/]$/.test(b64JsonMatch[1]));
          
          console.log(`JSON completeness check: openBraces=${openBraces}, closeBraces=${closeBraces}, hasCompleteJson=${hasCompleteJson}, hasCompleteB64=${hasCompleteB64}`);
          
          // Only try to process if we have what looks like a complete response
          if (hasCompleteJson && hasCompleteB64) {
            console.log("Detected complete JSON response, processing...");
            
            // Try to process what we have so far
            const response = cleanGatewayResponse(accumulatedData);
            
            // Check if we have an image (starts with ![)
            if (response.chunk.startsWith('![') && response.chunk.includes('base64,')) {
              // For large base64 images from GPT-image-1, convert to blob URL
              console.log("Found image markdown in response");
              
              // Extract base64 data from the markdown
              const base64Pattern = /data:image\/[^;]+;base64,([A-Za-z0-9+/=]+)/;
              const base64Match = response.chunk.match(base64Pattern);
              
              // Extract the alt text which contains the prompt
              const altTextMatch = response.chunk.match(/!\[(.*?)\]/);
              let altText = altTextMatch && altTextMatch[1] ? altTextMatch[1] : "Generated image";
              console.log("Extracted alt text/prompt:", altText);
              
              // For GPT-image-1, if there's no alt text or it's "Generated image", we need a better fallback
              if (altText === "Generated image" && isGptImageModel) {
                // Try to extract the prompt from modelName if it was passed in the format "model:prompt"
                const userPrompt = modelName.split(":")[1]?.trim();
                if (userPrompt) {
                  console.log("Using stored user prompt:", userPrompt);
                  altText = userPrompt;
                }
              }
              
              if (base64Match && base64Match[1] && base64Match[1].length > 100000) {
                try {
                  // Convert to blob URL for large images
                  const blobUrl = createBlobUrlFromBase64(base64Match[1]);
                  if (blobUrl) {
                    // Replace base64 with blob URL in the markdown but keep the original alt text
                    const blobMarkdown = `![${altText}](${blobUrl})`;
                    
                    // Clear initial message and send the image markdown with blob URL
                    console.log("Created blob URL for large image with prompt:", altText);
                    onNewChunk("\n\n" + blobMarkdown);
                    chunks.push(blobMarkdown);
                    break;
                  }
                } catch (error) {
                  console.error("Error creating blob URL:", error);
                  // Fall back to original markdown if blob URL creation fails
                  onNewChunk("\n\n" + response.chunk);
                  chunks.push(response.chunk);
                  break;
                }
              } else {
                // For smaller base64 images, use the original markdown
                onNewChunk("\n\n" + response.chunk);
                chunks.push(response.chunk);
                break;
              }
            } else {
              console.log("No image markdown found in complete response, trying fallback processing");
              // If cleanGatewayResponse didn't return an image, there might be an issue
              // Let's try a more direct approach
              try {
                const jsonData = JSON.parse(decodedData);
                if (jsonData && jsonData.data && jsonData.data[0] && jsonData.data[0].b64_json) {
                  const b64Data = jsonData.data[0].b64_json;
                  const prompt = jsonData.data[0].revised_prompt || "Generated image";
                  
                  // Extract user prompt from modelName if available
                  const userPrompt = modelName.split(":")[1]?.trim();
                  const finalPrompt = userPrompt || prompt;
                  
                  console.log("Direct JSON parsing successful, creating image markdown");
                  const imageMarkdown = `![${finalPrompt}](data:image/png;base64,${b64Data})`;
                  onNewChunk("\n\n" + imageMarkdown);
                  chunks.push(imageMarkdown);
                  break;
                }
              } catch (directParseError) {
                console.error("Direct JSON parsing failed:", directParseError);
              }
            }
          } else {
            console.log("JSON response appears incomplete, continuing to accumulate data");
            
            // Check for timeout - if we haven't received data for a while, process what we have
            const timeSinceLastChunk = Date.now() - lastChunkTime;
            if (timeSinceLastChunk > CHUNK_TIMEOUT_MS && accumulatedData.length > 10000) {
              console.log("Timeout detected, processing incomplete data as final attempt");
              
              // Try to process the incomplete data
              try {
                const incompleteData = new TextDecoder().decode(accumulatedData);
                const partialB64Match = incompleteData.match(/"b64_json"\s*:\s*"([A-Za-z0-9+/=]+)/);
                if (partialB64Match && partialB64Match[1] && partialB64Match[1].length > 1000) {
                  const partialB64 = partialB64Match[1];
                  const promptMatch = incompleteData.match(/"revised_prompt"\s*:\s*"([^"]+)"/);
                  const prompt = promptMatch && promptMatch[1] ? promptMatch[1] : "Generated image";
                  
                  const userPrompt = modelName.split(":")[1]?.trim();
                  const finalPrompt = userPrompt || prompt;
                  
                  console.log("Timeout recovery: Using partial base64 data, length:", partialB64.length);
                  const imageMarkdown = `![${finalPrompt}](data:image/png;base64,${partialB64})`;
                  onNewChunk("\n\n" + imageMarkdown);
                  chunks.push(imageMarkdown);
                  break;
                }
              } catch (timeoutError) {
                console.error("Timeout recovery failed:", timeoutError);
              }
            }
          }
        }
      } else {
        // Normal processing for other models - process each chunk separately
        const response = cleanGatewayResponse(value);
        chunks.push(response.chunk);
        citations = response.citations;
        
        // Extract document chunks if present and not already processed
        if (response.documentChunks && response.documentChunks.length > 0) {
          console.log("Found document chunks in response:", response.documentChunks.length);
          
          // Check if this is a batched response
          if (response.batchInfo) {
            console.log("Processing batched document chunks:", response.batchInfo);
            
            // Set up batch tracking on first batch
            if (expectedBatches === 0) {
              expectedBatches = response.batchInfo.total_batches;
              console.log("Expecting", expectedBatches, "total batches");
            }
            
            // Add chunks from this batch to accumulated chunks
            const mappedChunks = response.documentChunks.map((chunkData: any) => ({
              id: chunkData.id,
              document_title: chunkData.document_title || 'Unknown Document',
              document_source: chunkData.document_source,
              source_index: chunkData.source_index,
              document_id: chunkData.document_id,
              content: chunkData.content,
              url: chunkData.url,
              score: chunkData.score || 0,
              content_length: chunkData.content_length,
              metadata: chunkData.metadata || {}
            }));
            
            accumulatedDocumentChunks.push(...mappedChunks);
            receivedBatches++;
            
            console.log(`Received batch ${receivedBatches}/${expectedBatches}, total chunks: ${accumulatedDocumentChunks.length}`);
            
            // If this is the final batch, process all accumulated chunks
            if (response.batchInfo.is_final_batch || receivedBatches >= expectedBatches) {
              console.log("Final batch received, processing all document chunks:", accumulatedDocumentChunks.length);
              documentChunks = accumulatedDocumentChunks;
              
              // Call the callback with all accumulated document chunks
              onDocumentChunks(documentChunks);
            }
          } else {
            // Non-batched response - accumulate chunks instead of overwriting
            console.log("Processing non-batched document chunks");
            
            // Map the response chunks to our DocumentChunk interface
            const newChunks = response.documentChunks.map((chunkData: any) => ({
              id: chunkData.id,
              document_title: chunkData.document_title || 'Unknown Document',
              document_source: chunkData.document_source,
              source_index: chunkData.source_index,
              document_id: chunkData.document_id,
              content: chunkData.content,
              url: chunkData.url,
              score: chunkData.score || 0,
              content_length: chunkData.content_length,
              metadata: chunkData.metadata || {}
            }));
            
            // Accumulate chunks instead of overwriting
            documentChunks = [...documentChunks, ...newChunks];
            console.log("Accumulated document chunks (non-batched):", documentChunks.length);
            
            // Call the callback with the accumulated document chunks
            onDocumentChunks(documentChunks);
          }
        }
        
        onNewChunk(response.chunk);
      }
    }
  } catch (error) {
    console.error("Error reading stream:", error);
    // If we encounter an error but already started processing, continue
    if (chunks.length === 0) {
      const errorMessage = "Error reading response. Please try again.";
      onNewChunk(errorMessage);
      chunks.push(errorMessage);
    }
  }

  // If we couldn't find an image in the accumulated data but we have some data
  if (isGptImageModel && chunks.length === 0 && accumulatedData !== undefined) {
    console.log("Final attempt to process accumulated data:", accumulatedData.length, "bytes");
    
    // First try direct JSON parsing since the response might be complete but not detected as such
    try {
      const decodedData = new TextDecoder().decode(accumulatedData);
      console.log("Final attempt: trying direct JSON parsing on", decodedData.length, "characters");
      
      const jsonData = JSON.parse(decodedData);
      if (jsonData && jsonData.data && jsonData.data[0] && jsonData.data[0].b64_json) {
        const b64Data = jsonData.data[0].b64_json;
        const prompt = jsonData.data[0].revised_prompt || "Generated image";
        
        // Extract user prompt from modelName if available
        const userPrompt = modelName.split(":")[1]?.trim();
        const finalPrompt = userPrompt || prompt;
        
        console.log("Final attempt: Direct JSON parsing successful, creating image markdown");
        const imageMarkdown = `![${finalPrompt}](data:image/png;base64,${b64Data})`;
        onNewChunk("\n\n" + imageMarkdown);
        chunks.push(imageMarkdown);
        return {chunks: chunks.join(""), citations: citations, documentChunks};
      }
    } catch (directParseError) {
      console.log("Final attempt: Direct JSON parsing failed, trying cleanGatewayResponse:", directParseError);
    }
    
    // Fall back to cleanGatewayResponse
    // First try direct JSON parsing since the response might be complete but not detected as such
    try {
      const decodedData = new TextDecoder().decode(accumulatedData);
      console.log("Final attempt: trying direct JSON parsing on", decodedData.length, "characters");
      
      const jsonData = JSON.parse(decodedData);
      if (jsonData && jsonData.data && jsonData.data[0] && jsonData.data[0].b64_json) {
        const b64Data = jsonData.data[0].b64_json;
        const prompt = jsonData.data[0].revised_prompt || "Generated image";
        
        // Extract user prompt from modelName if available
        const userPrompt = modelName.split(":")[1]?.trim();
        const finalPrompt = userPrompt || prompt;
        
        console.log("Final attempt: Direct JSON parsing successful, creating image markdown");
        const imageMarkdown = `![${finalPrompt}](data:image/png;base64,${b64Data})`;
        onNewChunk("\n\n" + imageMarkdown);
        chunks.push(imageMarkdown);
        return {chunks: chunks.join(""), citations: citations, documentChunks};
      }
    } catch (directParseError) {
      console.log("Final attempt: Direct JSON parsing failed, trying cleanGatewayResponse:", directParseError);
    }
    
    // Fall back to cleanGatewayResponse
    const finalResponse = cleanGatewayResponse(accumulatedData);
    if (finalResponse.chunk.startsWith('![') && finalResponse.chunk.includes('base64,')) {
      console.log("Found image in final processing attempt");
      
      // Extract base64 data for large images
      const base64Pattern = /data:image\/[^;]+;base64,([A-Za-z0-9+/=]+)/;
      const base64Match = finalResponse.chunk.match(base64Pattern);
      
      // Extract the alt text which contains the prompt
      const altTextMatch = finalResponse.chunk.match(/!\[(.*?)\]/);
      let altText = altTextMatch && altTextMatch[1] ? altTextMatch[1] : "Generated image";
      console.log("Extracted alt text/prompt in final attempt:", altText);
      
      if (base64Match && base64Match[1] && base64Match[1].length > 100000) {
        try {
          // Convert to blob URL for large images
          const blobUrl = createBlobUrlFromBase64(base64Match[1]);
          if (blobUrl) {
            // Replace base64 with blob URL in the markdown but keep the original alt text
            const blobMarkdown = `![${altText}](${blobUrl})`;
            
            console.log("Created blob URL for large image in final attempt with prompt:", altText);
            onNewChunk(blobMarkdown);
            chunks.push(blobMarkdown);
          } else {
            onNewChunk(finalResponse.chunk);
            chunks.push(finalResponse.chunk);
          }
        } catch (error) {
          console.error("Error creating blob URL in final attempt:", error);
          // Fall back to original markdown
          onNewChunk(finalResponse.chunk);
          chunks.push(finalResponse.chunk);
        }
      } else {
        onNewChunk(finalResponse.chunk);
        chunks.push(finalResponse.chunk);
      }
    } else if (finalResponse.chunk.startsWith('![')) {
      console.log("Found image in final processing attempt");
      onNewChunk("\n\n" + finalResponse.chunk);
      chunks.push(finalResponse.chunk);
    } else {
      // Try to extract any base64 data as last resort
      try {
        const base64Pattern = /([A-Za-z0-9+/=]{1000,})/;
        // Use the string representation of accumulatedData instead of undefined 'decoded'
        const dataString = new TextDecoder().decode(accumulatedData);
        const base64Match = dataString.match(base64Pattern);
        
        if (base64Match && base64Match[1]) {
          const blobUrl = createBlobUrlFromBase64(base64Match[1]);
          if (blobUrl) {
            const blobMarkdown = `![Generated image](${blobUrl})`;
            console.log("Created blob URL from extracted base64 in last resort");
            onNewChunk(blobMarkdown);
            chunks.push(blobMarkdown);
            return {chunks: chunks.join(""), citations: citations, documentChunks};
          }
        }
      } catch (error) {
        console.error("Error in last-resort base64 extraction:", error);
      }
      
      // Fall back to generic message
      console.log("No image found in final processing attempt");
      const fallbackMessage = "Image was generated but couldn't be displayed correctly. The response format may not be supported.";
      onNewChunk(fallbackMessage);
      chunks.push(fallbackMessage);
    }
  } else if (isGptImageModel && chunks.length === 0) {
    // No accumulated data at all
    console.log("No data accumulated for image processing");
    const errorMessage = "No image data received from the server.";
    onNewChunk(errorMessage);
    chunks.push(errorMessage);
  }

  // Use accumulated chunks if we have them, otherwise use regular document chunks
  const finalDocumentChunks = accumulatedDocumentChunks.length > 0 ? accumulatedDocumentChunks : documentChunks;
  
  return {
    chunks: chunks.join(""), 
    citations: citations,
    documentChunks: finalDocumentChunks.length > 0 ? finalDocumentChunks : undefined
  };
}
